import Page from '../../../layouts/DashboardLayout/Page';
import { HiPencil, HiPlus, HiTrash } from 'react-icons/hi';
import { useUserSession } from '../../../components/UserSession';
import {
  useRuleControllerCreateRule,
  useRuleControllerDeleteRules,
  useRuleControllerGetRules,
  useRuleControllerUpdateRule,
  useTemplateControllerGetTemplates,
} from '../../../api/informApi/informApiComponents';
import {
  ContainsConditionValue,
  RatingConditionValue,
  SerializedRule,
  SerializedTemplate,
  WordCountConditionValue,
} from '../../../api/informApi/informApiSchemas';
import Card from '../../../components/Card';
import styles from './Rules.module.scss';
import Typography from '../../../components/Typography';
import IconButton from '../../../components/IconButton';
import {
  ComparatorTypes,
  ConditionTypes,
  comparatorLabels,
  comparatorOptions,
  conditionOptions,
  conditionPrefix,
  ratingOptions,
} from './constants';
import { keyBy, uniqueId } from 'lodash';
import { toast } from 'react-hot-toast';
import { Fragment, useState } from 'react';
import Popup, { PopupProps } from '../../../components/Popup';
import Input from '../../../components/Input';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import Dropdown from '../../../components/Dropdown';
import Button from '../../../components/Button';

interface RulePopupProps extends PopupProps {
  templates: SerializedTemplate[];
  rule: SerializedRule;
  refetch: () => Promise<any>;
}

function RulePopup({
  refetch,
  templates,
  show,
  onClose,
  rule,
}: RulePopupProps) {
  const { mutateAsync: updateRule, isLoading } = useRuleControllerUpdateRule();
  const { handleSubmit, register, reset, control } = useForm({
    defaultValues: rule,
  });
  const { fields, append, update, remove } = useFieldArray({
    name: 'conditions',
    control,
  });
  const userCtx = useUserSession();

  const closeAction = (_rule = rule) => {
    window.setTimeout(() => reset(_rule), 250);
    onClose?.();
  };

  return (
    <Popup className={styles.popup} onClose={() => closeAction()} show={show}>
      <form
        onSubmit={handleSubmit(async (updatedRule) => {
          await toast.promise(
            updateRule({
              pathParams: {
                id: userCtx.currentOrganization!,
                ruleId: rule.id,
              },
              body: {
                name: updatedRule.name,
                templateId: updatedRule.templateId,
                conditions: updatedRule.conditions.map((condition) => ({
                  type: condition.type as any,
                  ...condition.value,
                })),
              },
            }).then(() => refetch()),
            {
              loading: 'Updating rule...',
              success: 'Rule updated successfully!',
              error: 'Unable to update rule. Please try again later.',
            },
          );
          closeAction(updatedRule);
        })}
      >
        <Typography className={styles.popupLabel} textType="h2" as="h2">
          Edit Rule
        </Typography>
        <Input label="Name of Rule" {...register('name')} />
        <hr className={styles.divider} />
        <dl className={styles.formLayout}>
          {fields.map((field, idx) => {
            let render = null;
            switch (field.type) {
              case ConditionTypes.RATING:
                render = (
                  <>
                    <Controller
                      control={control}
                      name={`conditions.${idx}.value.comparator`}
                      defaultValue={ComparatorTypes.GT}
                      render={({ field }) => (
                        <Dropdown options={comparatorOptions} {...field} />
                      )}
                    />
                    <Controller
                      control={control}
                      name={`conditions.${idx}.value.rating`}
                      defaultValue={0}
                      render={({ field }) => (
                        <Dropdown
                          options={ratingOptions}
                          {...field}
                          onChange={({ currentTarget }) => {
                            field.onChange(parseInt(currentTarget.value));
                          }}
                        />
                      )}
                    />
                  </>
                );
                break;
              case ConditionTypes.REGEX:
                render = (
                  <Input
                    className={styles.fullField}
                    inputProps={{ placeholder: 'Enter Phrase' }}
                    {...register(`conditions.${idx}.value.regex`)}
                  />
                );
                break;
              case ConditionTypes.WORD_COUNT:
                render = (
                  <>
                    <Controller
                      control={control}
                      name={`conditions.${idx}.value.comparator`}
                      defaultValue={ComparatorTypes.EQ}
                      render={({ field }) => (
                        <Dropdown options={comparatorOptions} {...field} />
                      )}
                    />
                    <Input
                      inputProps={{
                        placeholder: 'Word Count',
                        type: 'number',
                      }}
                      {...register(`conditions.${idx}.value.count`, {
                        valueAsNumber: true,
                        value: 0,
                      })}
                    />
                  </>
                );
                break;
            }

            return (
              <Fragment key={field.id}>
                <Typography
                  className={styles.formLabel}
                  textWeight="bold"
                  textType="p2"
                  as="dt"
                >
                  {idx ? 'AND' : 'When'}
                </Typography>
                <dd className={styles.formValue}>
                  <Controller
                    control={control}
                    name={`conditions.${idx}.type`}
                    render={({ field: f }) => (
                      <Dropdown
                        options={conditionOptions}
                        {...f}
                        onChange={(e) => {
                          f.onChange(e);
                          update(idx, {
                            ...field,
                            type: e.currentTarget.value as ConditionTypes,
                          });
                        }}
                      />
                    )}
                  />
                  <div>
                    <IconButton
                      icon={HiTrash}
                      alt="Delete Condition"
                      onClick={() => remove(idx)}
                      buttonColor="error"
                      variant="ghost"
                    />
                  </div>
                </dd>
                <Typography
                  className={styles.formLabel}
                  textWeight="bold"
                  textType="p2"
                  as="dt"
                >
                  {conditionPrefix[field.type]}
                </Typography>
                <dd className={styles.formValue}>{render}</dd>
              </Fragment>
            );
          })}
          <Typography
            className={styles.formLabel}
            textColor="text-2"
            textWeight="bold"
            textType="p2"
            as="dt"
          >
            {fields.length ? 'AND' : 'When'}
          </Typography>
          <dd className={styles.formValue}>
            <Dropdown
              options={conditionOptions}
              onChange={({ currentTarget }) => {
                const type = currentTarget.value as ConditionTypes;
                append({
                  value: {} as any,
                  id: uniqueId(),
                  type,
                });
              }}
              value=""
            />
          </dd>
        </dl>
        <hr className={styles.divider} />
        <dl className={styles.formLayout}>
          <Typography
            className={styles.formLabel}
            textWeight="bold"
            textType="p2"
            as="dt"
          >
            Trigger
          </Typography>
          <dd className={styles.formValue}>
            <Controller
              control={control}
              name={`templateId`}
              render={({ field }) => (
                <Dropdown
                  options={templates.map((template) => ({
                    label: template.name,
                    value: template.id,
                  }))}
                  className={styles.fullField}
                  {...field}
                />
              )}
            />
          </dd>
        </dl>
        <div className={styles.formFooter}>
          <Button disabled={isLoading} variant="solid" buttonColor="success">
            Save Changes
          </Button>
          <Button
            disabled={isLoading}
            onClick={() => closeAction()}
            variant="outline"
            buttonColor="text-2"
            type="button"
          >
            Cancel
          </Button>
        </div>
      </form>
    </Popup>
  );
}

interface RuleProps {
  onDelete: (rule: SerializedRule) => Promise<void>;
  templates: SerializedTemplate[];
  template?: SerializedTemplate;
  refetch: () => Promise<any>;
  rule: SerializedRule;
  disabled: boolean;
}
function Rule({
  refetch,
  onDelete,
  templates,
  template,
  rule,
  disabled,
}: RuleProps) {
  const [editting, setEditting] = useState(false);
  return (
    <Card className={styles.container} backgroundColor="background-1" as="li">
      <div>
        <Typography
          className={styles.label}
          textWeight="bold"
          textType="p1"
          as="h3"
        >
          {rule.name}
        </Typography>
        <Typography
          className={styles.header}
          textWeight="bold"
          textType="p2"
          as="h4"
        >
          When
        </Typography>
        {rule.conditions.length ? (
          <ul className={styles.items}>
            {rule.conditions.map((condition) => {
              switch (condition.type) {
                case ConditionTypes.RATING: {
                  const values = condition.value as RatingConditionValue;
                  return (
                    <Typography
                      textColor="text-2"
                      textType="p3"
                      key={condition.id}
                      as="li"
                    >
                      Rating is{' '}
                      {comparatorLabels[values.comparator].toLowerCase()}{' '}
                      {values.rating}
                    </Typography>
                  );
                }
                case ConditionTypes.REGEX: {
                  const values = condition.value as ContainsConditionValue;
                  return (
                    <Typography
                      textColor="text-2"
                      textType="p3"
                      key={condition.id}
                      as="li"
                    >
                      Review contains "{values.regex}"
                    </Typography>
                  );
                }
                case ConditionTypes.WORD_COUNT: {
                  const values = condition.value as WordCountConditionValue;
                  return (
                    <Typography
                      textColor="text-2"
                      textType="p3"
                      key={condition.id}
                      as="li"
                    >
                      Review contains{' '}
                      {comparatorLabels[values.comparator].toLowerCase()}{' '}
                      {values.count} words
                    </Typography>
                  );
                }
                default:
                  console.warn(`Unknown action - ${condition.type}`);
                  return null;
              }
            })}
          </ul>
        ) : (
          <Typography textColor="warning" textType="p3">
            No conditions defined!
          </Typography>
        )}
        <Typography
          className={styles.header}
          textWeight="bold"
          textType="p2"
          as="h4"
        >
          Then
        </Typography>
        {template ? (
          <ul className={styles.items}>
            <Typography textColor="text-2" textType="p3" as="li">
              Respond with "{template.name}"
            </Typography>
          </ul>
        ) : (
          <Typography textColor="warning" textType="p3">
            No template defined!
          </Typography>
        )}
      </div>
      <div className={styles.actions}>
        <IconButton
          onClick={() => setEditting(true)}
          disabled={disabled}
          icon={HiPencil}
        />
        <IconButton
          onClick={() => onDelete(rule)}
          disabled={disabled}
          buttonColor="error"
          variant="solid"
          icon={HiTrash}
        />
      </div>
      <RulePopup
        templates={templates}
        refetch={refetch}
        onClose={() => setEditting(false)}
        rule={rule}
        show={editting}
      />
    </Card>
  );
}

function Rules() {
  const [hasAction, setHasAction] = useState(false);
  const userCtx = useUserSession();
  const { mutateAsync: createRule } = useRuleControllerCreateRule();
  const { mutateAsync: deleteRule } = useRuleControllerDeleteRules();
  const { data: templates, isLoading: isTemplatesLoading } =
    useTemplateControllerGetTemplates({
      pathParams: { id: userCtx.currentOrganization! },
      queryParams: {
        take: 100,
      },
    });
  const {
    data: rules,
    refetch,
    isLoading: isRulesLoading,
  } = useRuleControllerGetRules(
    {
      pathParams: { id: userCtx.currentOrganization! },
      queryParams: {
        take: 100,
      },
    },
    {
      enabled: !!userCtx.currentOrganization,
    },
  );

  const templatesById = keyBy(templates?.items ?? [], 'id');

  const onDeleteRule = async (rule: SerializedRule) => {
    setHasAction(true);
    await toast.promise(
      deleteRule({
        pathParams: {
          id: userCtx.currentOrganization!,
          ruleId: rule.id,
        },
      }).then(() => refetch()),
      {
        loading: 'Deleting rule...',
        success: 'Rule deleted successfully!',
        error: 'Unable to delete rule. Please try again later.',
      },
    );
    setHasAction(false);
  };

  return (
    <Page
      heading="Rules"
      description={[
        'Rules are conditions to look for within a specific review. Add rules to automate review responses based on the templates below.',
        'Only reviews matching a rule will receive a reply, otherwise, it will be left alone. Rules are ordered by priority.',
      ]}
      loading={isRulesLoading || isTemplatesLoading}
      actions={[
        {
          children: 'Create Rule',
          buttonColor: 'success',
          async onClick() {
            setHasAction(true);
            await toast.promise(
              createRule({
                pathParams: {
                  id: userCtx.currentOrganization!,
                },
                body: {
                  name: 'My New Rule',
                  conditions: [],
                },
              }).then(() => refetch()),
              {
                loading: 'Creating rule...',
                success: 'Rule created successfully!',
                error: 'Unable to create rule. Please try again later.',
              },
            );
            setHasAction(false);
          },
          variant: 'outline',
          icon: {
            placement: 'left',
            element: HiPlus,
          },
          disabled: hasAction,
        },
      ]}
    >
      <ul className={styles.list}>
        {rules?.items?.map((rule) => (
          <Rule
            onDelete={onDeleteRule}
            templates={templates?.items ?? []}
            template={templatesById[rule.templateId]}
            disabled={hasAction}
            refetch={refetch}
            rule={rule}
            key={rule.id}
          />
        ))}
      </ul>
    </Page>
  );
}

Rules.routeProps = {
  path: '/rules',
};
export default Rules;
